import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  useTheme, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  IconButton, 
  Tooltip, 
  useMediaQuery,
  LinearProgress
} from '@mui/material';
import { fetchOrderBook, updateOrderBook, setOrderBookError } from '../../store/Slices/assetSlice';
import { styled } from '@mui/system';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';

// Blinking effect for the current price with fixed color logic
const BlinkingTypography = styled(Typography)(({ theme, isBuyDominant }) => ({
  animation: `blink 1s infinite`,
  color: isBuyDominant ? theme.palette.success.main : theme.palette.error.main,
  '@keyframes blink': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.5 },
    '100%': { opacity: 1 },
  },
}));

const OrderBook = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Detect mobile screens
  const { light } = useSelector((state) => state.theme);
  const { selectedCoin } = useSelector((state) => state.spotTrading);
  const { orderBook, orderBookLoading: loading, error } = useSelector((state) => state.assets);
  const [hasFetched, setHasFetched] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(10);
  const [viewMode, setViewMode] = useState('both');
  const [fundingRate, setFundingRate] = useState(0);
  const [priceGroup, setPriceGroup] = useState(0); // 0 = no grouping, 0.01, 0.1, 1

  // Responsive font sizes and padding
  const fontSize = isMobile ? '0.75rem' : '0.875rem';
  const headerFontSize = isMobile ? '0.875rem' : '1rem';
  const paddingY = isMobile ? 0.3 : 0.5;
  const containerPadding = isMobile ? 1 : 2;

  // Log when the component mounts and unmounts
  useEffect(() => {
    console.log('OrderBook mounted', { selectedCoin });
    return () => {
      console.log('OrderBook unmounted', { selectedCoin });
    };
  }, [selectedCoin]);

  // Fetch order book data
  useEffect(() => {
    if (!selectedCoin) return;

    const fetchOrderBookData = async () => {
      try {
        const response = await fetch(`https://api.binance.com/api/v3/depth?symbol=${selectedCoin.toUpperCase()}&limit=50`);
        const data = await response.json();

        // Validate the API response structure
        if (!data || typeof data !== 'object' || !Array.isArray(data.bids) || !Array.isArray(data.asks)) {
          throw new Error('Invalid order book data format');
        }

        const formattedData = {
          bids: data.bids.map(([price, amount]) => ({
            price: parseFloat(price),
            amount: parseFloat(amount),
            total: parseFloat(price) * parseFloat(amount),
          })),
          asks: data.asks.map(([price, amount]) => ({
            price: parseFloat(price),
            amount: parseFloat(amount),
            total: parseFloat(price) * parseFloat(amount),
          })),
          currentPrice: data.bids.length > 0 ? parseFloat(data.bids[0][0]) : orderBook.currentPrice || 0,
        };

        // Ensure all entries have valid numbers
        formattedData.bids = formattedData.bids.filter(
          (bid) => !isNaN(bid.price) && !isNaN(bid.amount) && !isNaN(bid.total)
        );
        formattedData.asks = formattedData.asks.filter(
          (ask) => !isNaN(ask.price) && !isNaN(ask.amount) && !isNaN(ask.total)
        );

        dispatch(updateOrderBook(formattedData));
        setHasFetched(true);
      } catch (err) {
        console.error('Error fetching order book data:', err);
        dispatch(setOrderBookError('Failed to fetch order book data: ' + err.message));
      }
    };

    if (!hasFetched) {
      console.log('Initial fetch of order book for:', selectedCoin);
      dispatch(fetchOrderBook(selectedCoin));
      setHasFetched(true);
    }

    fetchOrderBookData();
    const fetchInterval = setInterval(fetchOrderBookData, 2000);

    return () => clearInterval(fetchInterval);
  }, [dispatch, selectedCoin, hasFetched, orderBook.currentPrice]);

  // Fetch funding rate
  useEffect(() => {
    if (!selectedCoin) return;

    const fetchFundingRate = async () => {
      try {
        const response = await fetch(`https://fapi.binance.com/fapi/v1/fundingRate?symbol=${selectedCoin.toUpperCase()}`);
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setFundingRate(parseFloat(data[data.length - 1].fundingRate));
        } else {
          setFundingRate(0);
        }
      } catch (error) {
        console.error('Error fetching funding rate:', error);
        setFundingRate(0);
      }
    };

    fetchFundingRate();
    const fundingRateInterval = setInterval(fetchFundingRate, 10000);

    return () => clearInterval(fundingRateInterval);
  }, [selectedCoin]);

  const handleRetry = () => {
    if (selectedCoin) {
      console.log('Retrying fetchOrderBook for:', selectedCoin);
      setHasFetched(false);
      dispatch(fetchOrderBook(selectedCoin));
    }
  };

  // Calculate total volume of Buy (Bids) and Sell (Asks) orders
  const totalBidsAmount = useMemo(() => {
    if (!orderBook.bids || !Array.isArray(orderBook.bids)) return 0;
    return orderBook.bids.reduce((sum, bid) => sum + (bid.amount || 0), 0);
  }, [orderBook.bids]);

  const totalAsksAmount = useMemo(() => {
    if (!orderBook.asks || !Array.isArray(orderBook.asks)) return 0;
    return orderBook.asks.reduce((sum, ask) => sum + (ask.amount || 0), 0);
  }, [orderBook.asks]);

  const totalVolume = totalBidsAmount + totalAsksAmount;
  const buyPressure = totalVolume ? (totalBidsAmount / totalVolume) * 100 : 50;
  const sellPressure = totalVolume ? (totalAsksAmount / totalVolume) * 100 : 50;

  // Determine if Buy orders dominate over Sell orders
  const isBuyDominant = totalBidsAmount > totalAsksAmount;

  // Helper function to format numbers based on price threshold
  const formatNumber = (value, isPriceOrTotal, priceThreshold = 10) => {
    if (value === undefined || value === null) return '0';
    const num = Number(value);
    // For prices and totals: if price < 10, show raw number; otherwise, use toFixed(2)
    if (isPriceOrTotal) {
      return num < priceThreshold ? num.toString() : num.toFixed(2);
    }
    // For amounts: if price < 10, show raw number; otherwise, use toFixed(4)
    return num < priceThreshold ? num.toString() : num.toFixed(4);
  };

  // Group orders by price level
  const groupOrders = (orders, groupSize) => {
    if (!Array.isArray(orders) || groupSize === 0) return orders || [];

    const grouped = {};
    orders.forEach((order) => {
      const price = order.price || 0;
      const roundedPrice = Math.floor(price / groupSize) * groupSize;
      if (!grouped[roundedPrice]) {
        grouped[roundedPrice] = { price: roundedPrice, amount: 0, total: 0 };
      }
      grouped[roundedPrice].amount += order.amount || 0;
      grouped[roundedPrice].total += order.total || 0;
    });

    return Object.values(grouped);
  };

  // Process Buy (Bids) orders with cumulative total and background width
  const processedBids = useMemo(() => {
    if (!orderBook.bids || !Array.isArray(orderBook.bids)) return [];
    const groupedBids = groupOrders([...orderBook.bids].sort((a, b) => (b.price || 0) - (a.price || 0)), priceGroup);
    const maxAmount = Math.max(
      ...groupedBids.map((bid) => bid.amount || 0),
      ...(orderBook.asks && Array.isArray(orderBook.asks) ? orderBook.asks.map((ask) => ask.amount || 0) : [0])
    );
    let cumulativeTotal = 0;
    return groupedBids.slice(0, displayLimit).map((bid) => {
      cumulativeTotal += bid.amount || 0;
      return {
        ...bid,
        cumulativeTotal,
        cumulativePercentage: totalVolume ? (cumulativeTotal / totalVolume) * 100 : 0,
        width: maxAmount ? ((bid.amount || 0) / maxAmount) * 100 : 0,
      };
    });
  }, [orderBook.bids, orderBook.asks, displayLimit, priceGroup, totalVolume]);

  // Process Sell (Asks) orders with cumulative total and background width
  const processedAsks = useMemo(() => {
    if (!orderBook.asks || !Array.isArray(orderBook.asks)) return [];
    const groupedAsks = groupOrders([...orderBook.asks].sort((a, b) => (a.price || 0) - (b.price || 0)), priceGroup);
    const maxAmount = Math.max(
      ...groupedAsks.map((ask) => ask.amount || 0),
      ...(orderBook.bids && Array.isArray(orderBook.bids) ? orderBook.bids.map((bid) => bid.amount || 0) : [0])
    );
    let cumulativeTotal = 0;
    return groupedAsks.slice(0, displayLimit).map((ask) => {
      cumulativeTotal += ask.amount || 0;
      return {
        ...ask,
        cumulativeTotal,
        cumulativePercentage: totalVolume ? (cumulativeTotal / totalVolume) * 100 : 0,
        width: maxAmount ? ((ask.amount || 0) / maxAmount) * 100 : 0,
      };
    });
  }, [orderBook.asks, orderBook.bids, displayLimit, priceGroup, totalVolume]);

  if (!selectedCoin) {
    return (
      <Box sx={{ p: containerPadding }}>
        <Typography sx={{ color: theme.palette.text.secondary, fontSize }}>
          Please select a coin to view the order book.
        </Typography>
      </Box>
    );
  }

  if (loading && !hasFetched) {
    return (
      <Box sx={{ p: containerPadding }}>
        <Typography sx={{ color: theme.palette.text.secondary, fontSize }}>
          Loading order book...
        </Typography>
      </Box>
    );
  }

  if (error) {
    console.log('OrderBook error:', error);
    return (
      <Box sx={{ p: containerPadding, textAlign: 'center' }}>
        <Typography sx={{ color: theme.palette.error.main, mb: 2, fontSize }}>
          Error fetching order book: {error}
        </Typography>
        <Button variant="contained" color="primary" onClick={handleRetry}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!processedBids.length && !processedAsks.length) {
    return (
      <Box sx={{ p: containerPadding }}>
        <Typography sx={{ color: theme.palette.text.secondary, fontSize }}>
          No data available in the order book at the moment.
        </Typography>
        <Button variant="contained" color="primary" onClick={handleRetry} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.paper,
        p: containerPadding,
        borderRadius: 2,
        boxShadow: light ? '0 2px 4px rgba(0, 0, 0, 0.1)' : '0 2px 4px rgba(0, 0, 0, 0.5)',
        fontFamily: 'Roboto, sans-serif',
      }}
    >
      {/* Funding Rate and Market Depth */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize }}>
          Funding Rate: {fundingRate.toFixed(4)}%
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: theme.palette.success.main, fontSize }}>
            Buy Pressure: {buyPressure.toFixed(2)}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={buyPressure}
            sx={{
              width: isMobile ? '100%' : 100,
              height: 6,
              bgcolor: theme.palette.error.main,
              '& .MuiLinearProgress-bar': { bgcolor: theme.palette.success.main },
            }}
          />
        </Box>
      </Box>

      {/* Header and Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 500, fontSize: isMobile ? '1rem' : '1.25rem' }}>
          Order Book - {selectedCoin}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Show Buy Orders Only">
            <IconButton
              onClick={() => setViewMode('bids')}
              sx={{
                bgcolor: viewMode === 'bids' ? 'rgba(0, 255, 0, 0.2)' : 'transparent',
                color: theme.palette.success.main,
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(0, 255, 0, 0.3)' },
              }}
            >
              <CandlestickChartIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Show Sell Orders Only">
            <IconButton
              onClick={() => setViewMode('asks')}
              sx={{
                bgcolor: viewMode === 'asks' ? 'rgba(255, 0, 0, 0.2)' : 'transparent',
                color: theme.palette.error.main,
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.3)' },
              }}
            >
              <CandlestickChartIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Show Both Buy and Sell Orders">
            <IconButton
              onClick={() => setViewMode('both')}
              sx={{
                bgcolor: viewMode === 'both' ? 'rgba(128, 128, 128, 0.2)' : 'transparent',
                color: theme.palette.text.primary,
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(128, 128, 128, 0.3)' },
              }}
            >
              <Box sx={{ display: 'flex' }}>
                <CandlestickChartIcon sx={{ color: theme.palette.success.main, fontSize: isMobile ? 'small' : 'medium' }} />
                <CandlestickChartIcon sx={{ color: theme.palette.error.main, ml: -1, fontSize: isMobile ? 'small' : 'medium' }} />
              </Box>
            </IconButton>
          </Tooltip>
          <FormControl size="small">
            <Select
              value={displayLimit}
              onChange={(e) => setDisplayLimit(e.target.value)}
              sx={{ height: isMobile ? 25 : 30, fontSize: isMobile ? '0.75rem' : '0.875rem' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <Select
              value={priceGroup}
              onChange={(e) => setPriceGroup(e.target.value)}
              sx={{ height: isMobile ? 25 : 30, fontSize: isMobile ? '0.75rem' : '0.875rem' }}
            >
              <MenuItem value={0}>No Grouping</MenuItem>
              <MenuItem value={0.01}>Group by 0.01</MenuItem>
              <MenuItem value={0.1}>Group by 0.1</MenuItem>
              <MenuItem value={1}>Group by 1</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Current Price at the Top if Showing Only Buy or Sell Orders */}
      {(viewMode === 'bids' || viewMode === 'asks') && orderBook.currentPrice && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: theme.palette.background.default,
            mb: 2,
          }}
        >
          <BlinkingTypography
            variant={isMobile ? 'h6' : 'h5'}
            isBuyDominant={isBuyDominant}
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}
          >
            {formatNumber(orderBook.currentPrice || 0, true)} USDT
            <Box component="span" sx={{ ml: 1 }}>
              {isBuyDominant ? <ArrowUpwardIcon fontSize={isMobile ? 'small' : 'medium'} /> : <ArrowDownwardIcon fontSize={isMobile ? 'small' : 'medium'} />}
            </Box>
          </BlinkingTypography>
        </Box>
      )}

      {/* Table Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, flex: 1, fontSize: headerFontSize }}>
          Price (USDT)
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, flex: 1, textAlign: 'center', fontSize: headerFontSize }}>
          Amount ({selectedCoin.replace('USDT', '')})
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, flex: 1, textAlign: 'right', fontSize: headerFontSize }}>
          Total (USDT)
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, flex: 1, textAlign: 'right', fontSize: headerFontSize }}>
          Depth (%)
        </Typography>
      </Box>

      {/* Sell Orders (Asks) */}
      {(viewMode === 'both' || viewMode === 'asks') && (
        <Box sx={{ maxHeight: viewMode === 'both' ? 200 : 400, overflowY: 'auto', mb: viewMode === 'both' ? 2 : 0 }}>
          {processedAsks.map((ask, index) => (
            <Tooltip
              key={`ask-${index}`}
              title={`Cumulative Depth: ${(ask.cumulativePercentage || 0).toFixed(2)}% of total volume`}
              placement="top"
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: paddingY,
                  position: 'relative',
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    height: '100%',
                    width: `${ask.width || 0}%`,
                    bgcolor: light ? 'rgba(255, 0, 0, 0.1)' : 'rgba(255, 0, 0, 0.2)',
                    zIndex: 0,
                  }}
                />
                <Typography sx={{ color: theme.palette.error.main, flex: 1, zIndex: 1, fontSize }}>
                  {formatNumber(ask.price || 0, true)}
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary, flex: 1, textAlign: 'center', zIndex: 1, fontSize }}>
                  {formatNumber(ask.amount || 0, false, ask.price || 0)}
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary, flex: 1, textAlign: 'right', zIndex: 1, fontSize }}>
                  {formatNumber(ask.total || 0, true)}
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary, flex: 1, textAlign: 'right', zIndex: 1, fontSize }}>
                  {(ask.cumulativePercentage || 0).toFixed(2)}%
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}

      {/* Current Price in the Middle if Showing Both */}
      {viewMode === 'both' && orderBook.currentPrice && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 1,
            borderTop: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: theme.palette.background.default,
          }}
        >
          <BlinkingTypography
            variant={isMobile ? 'h6' : 'h5'}
            isBuyDominant={isBuyDominant}
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}
          >
            {formatNumber(orderBook.currentPrice || 0, true)} USDT
            <Box component="span" sx={{ ml: 1 }}>
              {isBuyDominant ? <ArrowUpwardIcon fontSize={isMobile ? 'small' : 'medium'} /> : <ArrowDownwardIcon fontSize={isMobile ? 'small' : 'medium'} />}
            </Box>
          </BlinkingTypography>
        </Box>
      )}

      {/* Buy Orders (Bids) */}
      {(viewMode === 'both' || viewMode === 'bids') && (
        <Box sx={{ maxHeight: viewMode === 'both' ? 200 : 400, overflowY: 'auto', mt: viewMode === 'both' ? 2 : 0 }}>
          {processedBids.map((bid, index) => (
            <Tooltip
              key={`bid-${index}`}
              title={`Cumulative Depth: ${(bid.cumulativePercentage || 0).toFixed(2)}% of total volume`}
              placement="top"
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: paddingY,
                  position: 'relative',
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${bid.width || 0}%`,
                    bgcolor: light ? 'rgba(0, 255, 0, 0.1)' : 'rgba(0, 255, 0, 0.2)',
                    zIndex: 0,
                  }}
                />
                <Typography sx={{ color: theme.palette.success.main, flex: 1, zIndex: 1, fontSize }}>
                  {formatNumber(bid.price || 0, true)}
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary, flex: 1, textAlign: 'center', zIndex: 1, fontSize }}>
                  {formatNumber(bid.amount || 0, false, bid.price || 0)}
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary, flex: 1, textAlign: 'right', zIndex: 1, fontSize }}>
                  {formatNumber(bid.total || 0, true)}
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary, flex: 1, textAlign: 'right', zIndex: 1, fontSize }}>
                  {(bid.cumulativePercentage || 0).toFixed(2)}%
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default OrderBook;